import React, { useState, useRef, useEffect } from "react";
import { Paper, Text, Button, Group, Stack } from "@mantine/core";
import { IconUpload, IconCheck, IconX } from "@tabler/icons-react";
import { notifyError, notifySuccess } from "../../../utils/notify";
import { useApp } from "../../../context/AppContext";
import { useSalon } from "../../../context/SalonContext";
import { ImageUploadResponse } from "../../../types/staticWebGenerator";
import { BusinessInfo } from "../../../types/context";

// Color constants
const COLOR_BORDER_ACTIVE = "#1976d2";
const COLOR_BORDER_INACTIVE = "#dee2e6";
const COLOR_BACKGROUND_ACTIVE = "#e3f2fd";
const COLOR_BACKGROUND_INACTIVE = "#f8f9fa";
const COLOR_ICON_ACTIVE = "#1976d2";
const COLOR_ICON_INACTIVE = "#6c757d";

interface ImageUploadProps {
  title: string;
  description: string;
  buttonText: string;
  imageType?: "salon_logo" | "staff_photo";
  staffId?: string;
  onUpload?: (response: ImageUploadResponse) => void;
}

interface FileValidation {
  isValid: boolean;
  error?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  title,
  description,
  buttonText,
  imageType,
  staffId,
  onUpload,
}) => {
  const { uploadImage } = useApp();
  const { businessInfo } = useSalon() as { businessInfo?: BusinessInfo | null };
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  // Cleanup effect for component unmounting
  useEffect(() => {
    return () => {
      // Cleanup any pending uploads when component unmounts
      setIsUploading(false);
    };
  }, []);

  // File validation function
  const validateFile = (file: File): FileValidation => {
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/avif",
      "image/gif",
      "image/svg+xml",
    ];

    const allowedExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".webp",
      ".avif",
      ".gif",
      ".svg",
    ];

    const maxSize = 5 * 1024 * 1024; // 5MB in bytes

    // Check MIME type first
    if (!allowedTypes.includes(file.type)) {
      // Fallback to file extension check for better compatibility
      const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        return {
          isValid: false,
          error:
            "File type not supported. Please use JPEG, PNG, WebP, AVIF, GIF, or SVG",
        };
      }
    }

    if (file.size > maxSize) {
      return {
        isValid: false,
        error: "File too large. Please use files smaller than 5MB",
      };
    }

    return { isValid: true };
  };

  // Handle file selection (for button click)
  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  // Drag and drop event handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => {
      const newCount = prev - 1;
      if (newCount === 0) {
        setIsDragOver(false);
      }
      return newCount;
    });
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setDragCounter(0);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0]; // Take the first file
      await processFile(file);
    }
  };

  // Process file (reused for both drag and drop and button click)
  const processFile = async (file: File) => {
    // Prevent multiple simultaneous uploads
    if (isUploading) {
      notifyError("Please wait for the current upload to complete.", {
        title: "Upload Failed",
        autoClose: 3000,
      });
      return;
    }

    // Check if salon ID is available
    if (!businessInfo?.id) {
      notifyError("Salon information not available. Please refresh the page.", {
        title: "Upload Failed",
        autoClose: 5000,
      });
      return;
    }

    const validation = validateFile(file);
    if (!validation.isValid) {
      notifyError(validation.error, {
        title: "Upload Failed",
        autoClose: 5000,
      });
      return;
    }

    try {
      setIsUploading(true);

      const response = await uploadImage({
        image: file,
        business_location_id: businessInfo.id,
        optimization_type: "web",
        image_type: imageType,
        staff_id: staffId,
      });

      notifySuccess(`File uploaded successfully`, {
        title: "Image Successfully Uploaded",
        autoClose: 5000,
      });

      // Call the original onUpload callback if provided
      if (onUpload) {
        onUpload(response);
        setIsUploading(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    } catch (error) {
      console.error("Upload error:", error);

      // Provide more specific error messages based on error type
      let errorMessage = "Failed to upload image. Please try again.";
      if (error instanceof Error) {
        // Use the API error message if available (from fetchWithAuth)
        if (error.message && error.message !== "Failed to fetch data") {
          errorMessage = error.message;
        } else if (
          error.message.includes("network") ||
          error.message.includes("fetch")
        ) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        } else if (error.message.includes("timeout")) {
          errorMessage =
            "Upload timed out. Please try again with a smaller file.";
        }
      }

      notifyError(errorMessage, { title: "Upload Failed", autoClose: 5000 });
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Trigger file input
  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Paper p="md">
      <Text size="xl" fw={700} mb="md">
        {title}
      </Text>

      <Text c="dimmed" mb="lg" td="underline">
        {description}
      </Text>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/jpeg,image/jpg,image/png,image/webp,image/avif,image/gif,image/svg+xml"
        style={{ display: "none" }}
      />

      <Paper
        p="xl"
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        aria-label="Drag and drop area for image upload"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleButtonClick();
          }
        }}
        style={{
          border: isDragOver
            ? `2px dashed ${COLOR_BORDER_ACTIVE}`
            : `2px dashed ${COLOR_BORDER_INACTIVE}`,
          borderRadius: "8px",
          backgroundColor: isDragOver
            ? COLOR_BACKGROUND_ACTIVE
            : COLOR_BACKGROUND_INACTIVE,
          minHeight: "200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s ease-in-out",
          cursor: isDragOver ? "copy" : "pointer",
        }}
      >
        <Stack align="center" gap="md">
          <IconUpload
            size={48}
            color={isDragOver ? COLOR_ICON_ACTIVE : COLOR_ICON_INACTIVE}
          />
          <Text fw={500} size="lg" c={isDragOver ? "blue" : "dark"}>
            {isDragOver ? "Drop your file here" : "Upload a file"}
          </Text>
          <Text c="dimmed" size="sm">
            Drag and drop an image file here, or click to browse
          </Text>
          <Text c="dimmed" size="xs">
            Supports: JPEG, PNG, WebP, AVIF, GIF, SVG (max 5MB)
          </Text>
          <Button
            variant="light"
            color="gray"
            onClick={handleButtonClick}
            loading={isUploading}
            disabled={isUploading}
          >
            {isUploading ? "Uploading..." : buttonText}
          </Button>
        </Stack>
      </Paper>
    </Paper>
  );
};

export default ImageUpload;
